import React, { useState, useEffect } from 'react';
import { 
  CheckCircleIcon, 
  ClockIcon, 
  UserIcon, 
  CalendarIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  ArrowPathIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import FollowUpService from '../services/followUpService';

const FollowUpHistory = ({ leadId, refreshTrigger = 0 }) => {
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'completed'
  const [rescheduleModal, setRescheduleModal] = useState({ isOpen: false, followUp: null });
  const [rescheduleData, setRescheduleData] = useState({
    nextFollowUpDate: '',
    nextFollowUpTime: '',
    notes: '',
    reason: ''
  });
  const [completeModal, setCompleteModal] = useState({ isOpen: false, followUp: null });
  const [completionData, setCompletionData] = useState({
    outcome: 'successful',
    notes: ''
  });
  const [visibleHistoryIds, setVisibleHistoryIds] = useState(new Set());

  useEffect(() => {
    if (leadId) {
      fetchFollowUps();
    }
  }, [leadId, refreshTrigger]);

  const fetchFollowUps = async () => {
    try {
      setLoading(true);
      const result = await FollowUpService.getLeadFollowUps(leadId);
      
      if (result.success) {
        const formattedFollowUps = result.data.followUps.map(followUp => 
          FollowUpService.formatFollowUpForDisplay(followUp)
        );
        setFollowUps(formattedFollowUps);
      } else {
        setError(result.message);
      }
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
      setError('Failed to load follow-up history');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteClick = (followUp) => {
    setCompleteModal({ isOpen: true, followUp });
    setCompletionData({
      outcome: 'successful',
      notes: ''
    });
  };

  const handleCompleteSubmit = async () => {
    if (!completeModal.followUp) {
      alert('Follow-up data not found');
      return;
    }

    try {
      const updateData = {
        status: 'completed',
        completed: true,
        completedAt: new Date().toISOString(),
        outcome: completionData.outcome,
        notes: completionData.notes || completeModal.followUp.notes
      };

      const result = await FollowUpService.updateFollowUp(
        leadId,
        completeModal.followUp._id,
        updateData
      );
      
      if (result.success) {
        // Update local state
        setFollowUps(prev => prev.map(followUp => 
          followUp._id === completeModal.followUp._id 
            ? { ...followUp, ...updateData }
            : followUp
        ));
        
        // Close modal
        setCompleteModal({ isOpen: false, followUp: null });
        setCompletionData({ outcome: 'successful', notes: '' });
        
        // Refresh data
        await fetchFollowUps();
      }
    } catch (error) {
      console.error('Error completing follow-up:', error);
      alert('Failed to complete follow-up. Please try again.');
    }
  };

  const handleReschedule = (followUp) => {
    setRescheduleModal({ isOpen: true, followUp });
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setRescheduleData({
      nextFollowUpDate: tomorrow.toISOString().split('T')[0],
      nextFollowUpTime: '10:00',
      notes: '',
      reason: ''
    });
  };

  const handleRescheduleSubmit = async () => {
    if (!rescheduleData.nextFollowUpDate || !rescheduleData.nextFollowUpTime) {
      alert('Please select date and time');
      return;
    }

    try {
      const newDateTime = new Date(`${rescheduleData.nextFollowUpDate}T${rescheduleData.nextFollowUpTime}`);
      
      const updateData = {
        nextFollowUpDate: newDateTime.toISOString(),
        scheduledDate: newDateTime.toISOString(),
        notes: rescheduleData.notes || rescheduleModal.followUp.notes,
        status: 'pending',
        completed: false,
        rescheduled: true,
        rescheduledAt: new Date().toISOString(),
        rescheduledFrom: rescheduleModal.followUp.scheduledDate,
        rescheduleHistory: [
          ...(rescheduleModal.followUp.rescheduleHistory || []),
          {
            previousDate: rescheduleModal.followUp.scheduledDate,
            newDate: newDateTime.toISOString(),
            reason: rescheduleData.reason || 'Rescheduled',
            notes: rescheduleData.notes,
            rescheduledAt: new Date().toISOString()
          }
        ]
      };

      const result = await FollowUpService.updateFollowUp(leadId, rescheduleModal.followUp._id, updateData);
      
      if (result.success) {
        // Close modal
        setRescheduleModal({ isOpen: false, followUp: null });
        setRescheduleData({ nextFollowUpDate: '', nextFollowUpTime: '', notes: '', reason: '' });
        
        // Refresh data
        await fetchFollowUps();
      }
    } catch (error) {
      console.error('Error rescheduling follow-up:', error);
    }
  };

  const toggleHistoryVisibility = (followUpId) => {
    setVisibleHistoryIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(followUpId)) {
        newSet.delete(followUpId);
      } else {
        newSet.add(followUpId);
      }
      return newSet;
    });
  };

  const getFollowUpStatusIcon = (followUp) => {
    if (followUp.completed) {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    }
    
    if (followUp.nextFollowUpDate) {
      const nextDate = new Date(followUp.nextFollowUpDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (nextDate < today) {
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      }
    }
    
    return <ClockIcon className="h-5 w-5 text-yellow-500" />;
  };

  const getFollowUpStatusText = (followUp) => {
    if (followUp.completed) {
      return { text: 'Completed', color: 'text-green-600' };
    }
    
    if (followUp.nextFollowUpDate) {
      const nextDate = new Date(followUp.nextFollowUpDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (nextDate < today) {
        const daysOverdue = Math.floor((today - nextDate) / (1000 * 60 * 60 * 24));
        return { 
          text: `Overdue by ${daysOverdue} day${daysOverdue !== 1 ? 's' : ''}`, 
          color: 'text-red-600' 
        };
      }
      
      return { text: 'Pending', color: 'text-yellow-600' };
    }
    
    return { text: 'Pending', color: 'text-yellow-600' };
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center text-red-600">
          <ExclamationTriangleIcon className="h-8 w-8 mx-auto mb-2" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const pendingFollowUps = followUps.filter(f => !f.completed);
  const completedFollowUps = followUps.filter(f => f.completed);
  const displayFollowUps = activeTab === 'pending' ? pendingFollowUps : completedFollowUps;

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <ClockIcon className="h-5 w-5 mr-2 text-blue-600" />
            Follow-up History ({followUps.length})
          </h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">
              {completedFollowUps.length} completed
            </span>
            <span className="text-sm text-gray-600">•</span>
            <span className="text-sm text-gray-600">
              {pendingFollowUps.length} pending
            </span>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'pending'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ClockIcon className="h-4 w-4 inline mr-2" />
            Pending ({pendingFollowUps.length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'completed'
                ? 'bg-green-600 text-white shadow-md'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <CheckCircleIcon className="h-4 w-4 inline mr-2" />
            Completed ({completedFollowUps.length})
          </button>
        </div>
      </div>

      <div className="p-6">
        {displayFollowUps.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-gray-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              {activeTab === 'pending' ? (
                <ClockIcon className="h-10 w-10 text-gray-400" />
              ) : (
                <CheckCircleIcon className="h-10 w-10 text-gray-400" />
              )}
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              {activeTab === 'pending' ? 'No Pending Follow-ups' : 'No Completed Follow-ups'}
            </h4>
            <p className="text-gray-500 mb-4">
              {activeTab === 'pending' 
                ? 'All follow-ups are completed or no follow-ups added yet' 
                : 'Complete pending follow-ups to see them here'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {displayFollowUps.map((followUp, index) => {
              const statusInfo = getFollowUpStatusText(followUp);
              
              return (
                <div 
                  key={followUp._id || index}
                  className={`border rounded-xl p-5 transition-all shadow-sm hover:shadow-md ${
                    followUp.completed 
                      ? 'border-green-200 bg-gradient-to-r from-green-50 to-green-100' 
                      : statusInfo.color === 'text-red-600'
                      ? 'border-red-200 bg-gradient-to-r from-red-50 to-red-100'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="flex-shrink-0 mt-1">
                        {getFollowUpStatusIcon(followUp)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <span className="text-2xl">{followUp.typeIcon}</span>
                            </div>
                            <div>
                              <h4 className="text-base font-semibold text-gray-900">
                                {followUp.summary}
                              </h4>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${followUp.priorityColor}`}>
                                  {followUp.priority}
                                </span>
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                  followUp.completed 
                                    ? 'bg-green-100 text-green-800'
                                    : statusInfo.color === 'text-red-600'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {statusInfo.text}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {followUp.notes && (
                          <div className="bg-gray-50 rounded-lg p-3 mb-3">
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {followUp.notes}
                            </p>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                          <div className="flex items-center text-gray-600">
                            <CalendarIcon className="h-4 w-4 mr-2 text-blue-500" />
                            <span className="font-medium">Date:</span>
                            <span className="ml-1">{followUp.formattedDate}</span>
                          </div>
                          
                          {followUp.addedByName && (
                            <div className="flex items-center text-gray-600">
                              <UserIcon className="h-4 w-4 mr-2 text-purple-500" />
                              <span className="font-medium">By:</span>
                              <span className="ml-1">{followUp.addedByName}</span>
                              <span className="text-gray-400 ml-1">({followUp.addedByRole})</span>
                            </div>
                          )}
                          
                          <div className="flex items-center">
                            <div className={`flex items-center ${statusInfo.color}`}>
                              {getFollowUpStatusIcon(followUp)}
                              <span className="ml-2 font-medium">{statusInfo.text}</span>
                            </div>
                          </div>
                        </div>
                        
                        {followUp.nextFollowUpDate && (
                          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-center text-blue-800">
                              <ClockIcon className="h-4 w-4 mr-2" />
                              <span className="font-semibold">Next Follow-up:</span>
                              <span className="ml-2">{followUp.formattedNextDate}</span>
                            </div>
                            {followUp.nextStep && (
                              <div className="mt-2 text-blue-700">
                                <span className="font-semibold">Next Step:</span>
                                <span className="ml-2">{followUp.nextStep}</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Show Reschedule History Button */}
                        {followUp.rescheduleHistory && followUp.rescheduleHistory.length > 0 && (
                          <button
                            onClick={() => toggleHistoryVisibility(followUp._id)}
                            className="mt-3 inline-flex items-center px-3 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-all duration-200 text-sm font-medium border border-amber-300"
                          >
                            <ArrowPathIcon className="h-4 w-4 mr-2" />
                            <span>
                              {visibleHistoryIds.has(followUp._id) ? 'Hide' : 'Show'} Reschedule History ({followUp.rescheduleHistory.length})
                            </span>
                          </button>
                        )}

                        {/* Reschedule History - Conditional Display */}
                        {followUp.rescheduleHistory && followUp.rescheduleHistory.length > 0 && visibleHistoryIds.has(followUp._id) && (
                          <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <div className="flex items-center text-amber-800 mb-2">
                              <ArrowPathIcon className="h-4 w-4 mr-2" />
                              <span className="font-semibold">Reschedule History ({followUp.rescheduleHistory.length})</span>
                            </div>
                            <div className="space-y-2">
                              {followUp.rescheduleHistory.map((reschedule, idx) => (
                                <div key={idx} className="text-sm bg-white p-2 rounded border border-amber-100">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <span className="text-gray-600">From:</span>
                                      <span className="ml-1 font-medium text-gray-900">
                                        {new Date(reschedule.previousDate).toLocaleDateString('en-IN')}
                                      </span>
                                      <span className="mx-2 text-gray-400">→</span>
                                      <span className="text-gray-600">To:</span>
                                      <span className="ml-1 font-medium text-gray-900">
                                        {new Date(reschedule.newDate).toLocaleDateString('en-IN')}
                                      </span>
                                    </div>
                                    <span className="text-xs text-gray-500">
                                      {new Date(reschedule.rescheduledAt).toLocaleDateString('en-IN')}
                                    </span>
                                  </div>
                                  {reschedule.reason && (
                                    <div className="mt-1 text-gray-700">
                                      <span className="font-medium">Reason:</span> {reschedule.reason}
                                    </div>
                                  )}
                                  {reschedule.notes && (
                                    <div className="mt-1 text-gray-600 text-xs">
                                      {reschedule.notes}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end space-y-2 mt-4">
                      {!followUp.completed && (
                        <>
                          <button
                            onClick={() => handleCompleteClick(followUp)}
                            className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                          >
                            <CheckCircleIcon className="h-4 w-4 mr-2" />
                            Mark Complete
                          </button>
                          <button
                            onClick={() => handleReschedule(followUp)}
                            className="inline-flex items-center px-3 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors"
                          >
                            <ArrowPathIcon className="h-4 w-4 mr-2" />
                            Reschedule
                          </button>
                        </>
                      )}
                      {followUp.completed && (
                        <div className="inline-flex items-center px-3 py-2 bg-green-100 text-green-800 text-sm font-medium rounded-lg">
                          <CheckCircleIcon className="h-4 w-4 mr-2" />
                          Completed
                          {followUp.completedAt && (
                            <span className="ml-2 text-xs">
                              ({new Date(followUp.completedAt).toLocaleDateString('en-IN')})
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reschedule Modal */}
      {rescheduleModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Reschedule Follow-up</h3>
              <button
                onClick={() => setRescheduleModal({ isOpen: false, followUp: null })}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Current Follow-up Info */}
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Current Summary:</span> {rescheduleModal.followUp?.summary}
                </p>
                {rescheduleModal.followUp?.nextFollowUpDate && (
                  <p className="text-sm text-gray-700 mt-1">
                    <span className="font-medium">Current Date:</span>{' '}
                    {new Date(rescheduleModal.followUp.nextFollowUpDate).toLocaleDateString('en-IN')}
                  </p>
                )}
              </div>

              {/* New Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Follow-up Date *
                </label>
                <input
                  type="date"
                  value={rescheduleData.nextFollowUpDate}
                  onChange={(e) => setRescheduleData(prev => ({ ...prev, nextFollowUpDate: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* New Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Follow-up Time *
                </label>
                <input
                  type="time"
                  value={rescheduleData.nextFollowUpTime}
                  onChange={(e) => setRescheduleData(prev => ({ ...prev, nextFollowUpTime: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Rescheduling
                </label>
                <input
                  type="text"
                  value={rescheduleData.reason}
                  onChange={(e) => setRescheduleData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="e.g., Client requested, Conflict in schedule..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes
                </label>
                <textarea
                  value={rescheduleData.notes}
                  onChange={(e) => setRescheduleData(prev => ({ ...prev, notes: e.target.value }))}
                  rows="3"
                  placeholder="Any additional information..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Reschedule History Count */}
              {rescheduleModal.followUp?.rescheduleHistory && rescheduleModal.followUp.rescheduleHistory.length > 0 && (
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <ArrowPathIcon className="h-4 w-4 inline mr-1" />
                    This follow-up has been rescheduled {rescheduleModal.followUp.rescheduleHistory.length} time(s) before
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setRescheduleModal({ isOpen: false, followUp: null })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRescheduleSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
              >
                <ArrowPathIcon className="h-4 w-4 inline mr-2" />
                Reschedule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Follow-up Modal */}
      {completeModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Complete Follow-up</h3>
              <button
                onClick={() => setCompleteModal({ isOpen: false, followUp: null })}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Current Follow-up Info */}
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Summary:</span> {completeModal.followUp?.summary}
                </p>
                <p className="text-sm text-gray-700 mt-1">
                  <span className="font-medium">Lead:</span> {completeModal.followUp?.leadName}
                </p>
              </div>

              {/* Outcome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Outcome *
                </label>
                <select
                  value={completionData.outcome}
                  onChange={(e) => setCompletionData(prev => ({ ...prev, outcome: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="successful">✅ Successful</option>
                  <option value="unsuccessful">❌ Unsuccessful</option>
                  <option value="needs_followup">🔄 Needs Follow-up</option>
                  <option value="no_response">📵 No Response</option>
                </select>
              </div>

              {/* Completion Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Completion Notes
                </label>
                <textarea
                  value={completionData.notes}
                  onChange={(e) => setCompletionData(prev => ({ ...prev, notes: e.target.value }))}
                  rows="4"
                  placeholder="What happened during this follow-up? Any important details..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setCompleteModal({ isOpen: false, followUp: null })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCompleteSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
              >
                <CheckCircleIcon className="h-4 w-4 inline mr-2" />
                Mark Complete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowUpHistory;
